import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import * as ImageManipulator from "expo-image-manipulator";
import { readAsStringAsync } from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useChat } from "@/modules/chat/hooks/useChat";
import { supabase } from "@/lib/supabase/client";
import { Loader } from "@/components/Loader";
import type { ChatParticipant, Profile } from "@/modules/chat/types";

/**
 * GroupSettingsScreen
 * Manage group chat: name, avatar, members
 * Only group creator and super admins can manage
 */
export default function GroupSettingsScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { chatId } = useLocalSearchParams<{ chatId: string }>();
  const { user, roles } = useAuth();
  const userId = user?.id;

  const { chat, loading, getChatName } = useChat(chatId, userId);

  // Edit state
  const [groupName, setGroupName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Add member modal
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [allUsers, setAllUsers] = useState<Profile[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);

  /** Determine if current user can manage group */
  const isCreator = chat?.created_by === userId;
  const isSuperAdmin = roles.isSuperAdmin;
  const canManage = isCreator || isSuperAdmin;

  /** Active participants (not left) */
  const activeParticipants = chat?.participants?.filter((p) => !p.left_at) || [];

  /** Sync chat data to local state */
  useEffect(() => {
    if (chat) {
      setGroupName(chat.name || "");
      setAvatarUrl(chat.avatar_url || null);
    }
  }, [chat]);

  /**
   * Save group name
   */
  const handleSaveName = useCallback(async () => {
    if (!chatId || !groupName.trim()) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("chats")
        .update({ name: groupName.trim(), updated_at: new Date().toISOString() })
        .eq("id", chatId);

      if (error) throw error;
      setIsEditingName(false);
    } catch (error) {
      console.error("Error updating group name:", error);
      Alert.alert("Fout", "Kon groepsnaam niet wijzigen");
    } finally {
      setIsSaving(false);
    }
  }, [chatId, groupName]);

  /**
   * Pick and upload group avatar
   * iOS: uses native crop UI (works well)
   * Android: skips native crop (dark UI issue), auto-crops to square via ImageManipulator
   */
  const handlePickAvatar = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Geen toegang", "We hebben toegang nodig tot je foto's.");
        return;
      }

      const isIOS = Platform.OS === "ios";

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: isIOS,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setIsUploadingAvatar(true);

        let uri = result.assets[0].uri;

        // On Android, auto-crop to a centered square
        if (!isIOS) {
          const { width, height } = result.assets[0];
          const size = Math.min(width, height);
          const originX = Math.floor((width - size) / 2);
          const originY = Math.floor((height - size) / 2);

          const manipulated = await ImageManipulator.manipulateAsync(
            uri,
            [
              { crop: { originX, originY, width: size, height: size } },
              { resize: { width: 400 } },
            ],
            { compress: 0.8, format: ImageManipulator.SaveFormat.JPEG }
          );
          uri = manipulated.uri;
        }

        const base64 = await readAsStringAsync(uri, { encoding: "base64" });
        const fileName = `chat-avatars/${chatId}/avatar-${Date.now()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from("chat-files")
          .upload(fileName, decode(base64), {
            contentType: "image/jpeg",
            upsert: true,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          Alert.alert("Fout", "Kon afbeelding niet uploaden");
          setIsUploadingAvatar(false);
          return;
        }

        const { data: urlData } = supabase.storage
          .from("chat-files")
          .getPublicUrl(fileName);

        const newAvatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;

        // Update chat record
        const { error: updateError } = await supabase
          .from("chats")
          .update({ avatar_url: newAvatarUrl, updated_at: new Date().toISOString() })
          .eq("id", chatId);

        if (updateError) {
          console.error("Error updating avatar:", updateError);
          Alert.alert("Fout", "Kon avatar niet bijwerken");
        } else {
          setAvatarUrl(newAvatarUrl);
        }

        setIsUploadingAvatar(false);
      }
    } catch (error) {
      console.error("Error picking avatar:", error);
      Alert.alert("Fout", "Kon afbeelding niet laden");
      setIsUploadingAvatar(false);
    }
  }, [chatId]);

  /**
   * Fetch all users for add member modal
   */
  const fetchAvailableUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, nickname, avatar_url")
        .order("full_name");

      if (error) throw error;

      // Filter out current participants
      const participantIds = new Set(activeParticipants.map((p) => p.user_id));
      const available = (data || []).filter((u) => !participantIds.has(u.id));
      setAllUsers(available);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [activeParticipants]);

  /**
   * Add a member to the group
   */
  const handleAddMember = useCallback(
    async (memberId: string) => {
      if (!chatId) return;

      try {
        const { error } = await supabase.from("chat_participants").insert({
          chat_id: chatId,
          user_id: memberId,
          is_admin: false,
        });

        if (error) throw error;

        // Remove from available list
        setAllUsers((prev) => prev.filter((u) => u.id !== memberId));
        Alert.alert("Gelukt", "Lid toegevoegd aan de groep");

        // Reload chat data
        router.replace({ pathname: "/chat-screens/group-settings", params: { chatId } });
      } catch (error) {
        console.error("Error adding member:", error);
        Alert.alert("Fout", "Kon lid niet toevoegen");
      }
    },
    [chatId, router]
  );

  /**
   * Remove a member from the group
   */
  const handleRemoveMember = useCallback(
    (participant: ChatParticipant) => {
      if (!canManage || participant.user_id === userId) return;

      const memberName = participant.user?.nickname || participant.user?.full_name || "dit lid";

      Alert.alert(
        "Lid verwijderen",
        `Weet je zeker dat je ${memberName} uit de groep wilt verwijderen?`,
        [
          { text: "Annuleer", style: "cancel" },
          {
            text: "Verwijderen",
            style: "destructive",
            onPress: async () => {
              try {
                const { error } = await supabase
                  .from("chat_participants")
                  .update({ left_at: new Date().toISOString() })
                  .eq("chat_id", chatId)
                  .eq("user_id", participant.user_id);

                if (error) throw error;

                router.replace({ pathname: "/chat-screens/group-settings", params: { chatId } });
              } catch (error) {
                console.error("Error removing member:", error);
                Alert.alert("Fout", "Kon lid niet verwijderen");
              }
            },
          },
        ]
      );
    },
    [canManage, chatId, userId, router]
  );

  /**
   * Leave the group
   */
  const handleLeaveGroup = useCallback(() => {
    Alert.alert(
      "Groep verlaten",
      "Weet je zeker dat je deze groep wilt verlaten?",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verlaten",
          style: "destructive",
          onPress: async () => {
            try {
              const { error } = await supabase
                .from("chat_participants")
                .update({ left_at: new Date().toISOString() })
                .eq("chat_id", chatId)
                .eq("user_id", userId);

              if (error) throw error;

              router.dismissAll();
            } catch (error) {
              console.error("Error leaving group:", error);
              Alert.alert("Fout", "Kon groep niet verlaten");
            }
          },
        },
      ]
    );
  }, [chatId, userId, router]);

  /**
   * Open add member modal
   */
  const handleOpenAddMember = useCallback(() => {
    setShowAddMemberModal(true);
    setSearchQuery("");
    fetchAvailableUsers();
  }, [fetchAvailableUsers]);

  /** Filtered users based on search */
  const filteredUsers = allUsers.filter((u) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(query) ||
      u.nickname?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center">
          <Loader size={60} />
        </View>
      </SafeAreaView>
    );
  }

  if (!chat || chat.type !== "group") {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="alert-circle" size={48} color={colors.error} />
          <Text className="text-center mt-4" style={{ color: colors.error }}>
            Groep niet gevonden
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View
        className="flex-row items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text className="text-lg font-bold" style={{ color: colors.text }}>
          Groepsinstellingen
        </Text>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* Group Avatar & Name */}
        <View className="items-center py-6">
          {/* Avatar */}
          <TouchableOpacity
            onPress={canManage ? handlePickAvatar : undefined}
            disabled={!canManage || isUploadingAvatar}
            className="relative"
            accessibilityLabel="Groepsfoto wijzigen"
          >
            {isUploadingAvatar ? (
              <View
                className="w-24 h-24 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : avatarUrl ? (
              <Image
                source={{ uri: avatarUrl }}
                style={{ width: 96, height: 96, borderRadius: 48 }}
                contentFit="cover"
              />
            ) : (
              <View
                className="w-24 h-24 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary }}
              >
                <Text className="text-white text-3xl font-bold">
                  {(groupName || "G").charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            {canManage && (
              <View
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full items-center justify-center border-2"
                style={{
                  backgroundColor: colors.primary,
                  borderColor: colors.background,
                }}
              >
                <Ionicons name="camera" size={16} color="#FFFFFF" />
              </View>
            )}
          </TouchableOpacity>

          {/* Name */}
          {isEditingName ? (
            <View className="flex-row items-center mt-4 px-8">
              <TextInput
                value={groupName}
                onChangeText={setGroupName}
                className="flex-1 text-center text-xl font-bold px-4 py-2 rounded-xl"
                style={{
                  color: colors.text,
                  backgroundColor: colors.surfaceSecondary,
                }}
                autoFocus
                returnKeyType="done"
                onSubmitEditing={handleSaveName}
              />
              <TouchableOpacity
                onPress={handleSaveName}
                disabled={isSaving}
                className="ml-2 p-2"
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="checkmark-circle" size={28} color={colors.primary} />
                )}
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  setGroupName(chat.name || "");
                  setIsEditingName(false);
                }}
                className="ml-1 p-2"
              >
                <Ionicons name="close-circle" size={28} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              onPress={canManage ? () => setIsEditingName(true) : undefined}
              disabled={!canManage}
              className="flex-row items-center mt-4"
            >
              <Text className="text-xl font-bold" style={{ color: colors.text }}>
                {groupName || "Naamloze groep"}
              </Text>
              {canManage && (
                <Ionicons
                  name="pencil"
                  size={18}
                  color={colors.textSecondary}
                  style={{ marginLeft: 8 }}
                />
              )}
            </TouchableOpacity>
          )}

          <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            {activeParticipants.length} deelnemers
          </Text>
        </View>

        {/* Shared Media Link */}
        <TouchableOpacity
          onPress={() =>
            router.push({ pathname: "/chat-detail", params: { chatId } } as any)
          }
          className="flex-row items-center px-4 py-4 mx-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.surfaceSecondary }}
          accessibilityLabel="Gedeelde media bekijken"
          accessibilityRole="button"
        >
          <View
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primaryLight }}
          >
            <Ionicons name="images" size={20} color={colors.primary} />
          </View>
          <Text className="flex-1 ml-3 font-medium" style={{ color: colors.text }}>
            Gedeelde media & polls
          </Text>
          <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
        </TouchableOpacity>

        {/* Members Section */}
        <View className="px-4">
          <View className="flex-row items-center justify-between mb-3">
            <Text className="text-sm font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
              Leden ({activeParticipants.length})
            </Text>
            {canManage && (
              <TouchableOpacity
                onPress={handleOpenAddMember}
                className="flex-row items-center px-3 py-1.5 rounded-full"
                style={{ backgroundColor: colors.primary }}
                accessibilityLabel="Lid toevoegen"
                accessibilityRole="button"
              >
                <Ionicons name="person-add" size={16} color="#FFFFFF" />
                <Text className="ml-1.5 text-sm font-medium text-white">
                  Toevoegen
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {activeParticipants.map((participant) => {
            const isParticipantCreator = participant.user_id === chat.created_by;
            const isParticipantAdmin = participant.is_admin;
            const isCurrentUser = participant.user_id === userId;
            const displayName =
              participant.user?.nickname || participant.user?.full_name || "Onbekend";

            return (
              <View
                key={participant.id}
                className="flex-row items-center py-3 px-3 rounded-xl mb-2"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                {/* Avatar */}
                {participant.user?.avatar_url ? (
                  <Image
                    source={{ uri: participant.user.avatar_url }}
                    style={{ width: 44, height: 44, borderRadius: 22 }}
                    contentFit="cover"
                  />
                ) : (
                  <View
                    className="w-11 h-11 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Text className="text-white text-lg font-bold">
                      {displayName.charAt(0).toUpperCase()}
                    </Text>
                  </View>
                )}

                {/* Name & Role */}
                <View className="flex-1 ml-3">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    {displayName}
                    {isCurrentUser && (
                      <Text style={{ color: colors.textSecondary }}> (jij)</Text>
                    )}
                  </Text>
                  {(isParticipantCreator || isParticipantAdmin) && (
                    <Text className="text-xs" style={{ color: colors.primary }}>
                      {isParticipantCreator ? "Beheerder" : "Admin"}
                    </Text>
                  )}
                </View>

                {/* Remove button (not for self, not for creator) */}
                {canManage && !isCurrentUser && !isParticipantCreator && (
                  <TouchableOpacity
                    onPress={() => handleRemoveMember(participant)}
                    className="p-2"
                    accessibilityLabel={`${displayName} verwijderen`}
                  >
                    <Ionicons name="remove-circle-outline" size={24} color={colors.error} />
                  </TouchableOpacity>
                )}
              </View>
            );
          })}
        </View>

        {/* Leave Group */}
        <View className="px-4 mt-6 mb-8">
          <TouchableOpacity
            onPress={handleLeaveGroup}
            className="flex-row items-center justify-center py-4 rounded-xl"
            style={{ backgroundColor: colors.errorLight }}
            accessibilityLabel="Groep verlaten"
            accessibilityRole="button"
          >
            <Ionicons name="exit-outline" size={22} color={colors.error} />
            <Text className="ml-2 font-semibold" style={{ color: colors.error }}>
              Groep verlaten
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Add Member Modal */}
      <Modal
        visible={showAddMemberModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowAddMemberModal(false)}
      >
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity onPress={() => setShowAddMemberModal(false)}>
              <Text style={{ color: colors.primary }}>Sluiten</Text>
            </TouchableOpacity>
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              Lid toevoegen
            </Text>
            <View style={{ width: 50 }} />
          </View>

          {/* Search */}
          <View className="px-4 py-3">
            <View
              className="flex-row items-center px-4 py-2.5 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary }}
            >
              <Ionicons name="search" size={18} color={colors.textSecondary} />
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Zoek familielid..."
                placeholderTextColor={colors.textTertiary}
                className="flex-1 ml-2"
                style={{ color: colors.text }}
                autoCapitalize="none"
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery("")}>
                  <Ionicons name="close-circle" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* User List */}
          {loadingUsers ? (
            <View className="flex-1 items-center justify-center">
              <Loader size={40} />
            </View>
          ) : (
            <FlatList
              data={filteredUsers}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => (
                <TouchableOpacity
                  onPress={() => handleAddMember(item.id)}
                  className="flex-row items-center px-4 py-3 border-b"
                  style={{ borderColor: colors.border }}
                  accessibilityLabel={`${item.full_name || item.nickname} toevoegen`}
                  accessibilityRole="button"
                >
                  {item.avatar_url ? (
                    <Image
                      source={{ uri: item.avatar_url }}
                      style={{ width: 44, height: 44, borderRadius: 22 }}
                      contentFit="cover"
                    />
                  ) : (
                    <View
                      className="w-11 h-11 rounded-full items-center justify-center"
                      style={{ backgroundColor: colors.primary }}
                    >
                      <Text className="text-white text-lg font-bold">
                        {(item.full_name || "?").charAt(0).toUpperCase()}
                      </Text>
                    </View>
                  )}
                  <Text className="flex-1 ml-3 font-medium" style={{ color: colors.text }}>
                    {item.nickname || item.full_name || "Onbekend"}
                  </Text>
                  <View
                    className="px-3 py-1.5 rounded-full"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    <Text className="text-sm font-medium" style={{ color: colors.primary }}>
                      Toevoegen
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
              ListEmptyComponent={
                <View className="flex-1 items-center justify-center py-12">
                  <Ionicons name="people-outline" size={48} color={colors.textTertiary} />
                  <Text className="mt-4 text-center" style={{ color: colors.textSecondary }}>
                    {searchQuery ? "Geen familieleden gevonden" : "Alle familieleden zijn al lid"}
                  </Text>
                </View>
              }
              contentContainerStyle={{ flexGrow: 1 }}
            />
          )}
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}
